import { effect, inject, Injectable, InjectionToken, signal, WritableSignal } from '@angular/core';

export type StorageMode = 'keys' | 'blob';

export interface StorageConfig {
  namespace: string;
  mode?: StorageMode;
  blobKey?: string;
  backend?: 'local' | 'session';
}

export const STORAGE_CONFIG = new InjectionToken<StorageConfig>('STORAGE_CONFIG');

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  private readonly cfg = inject(STORAGE_CONFIG, { optional: true }) ?? { namespace: 'app' };
  private readonly mode: StorageMode = this.cfg.mode ?? 'keys';
  private readonly storage: StorageLike = createStorage(this.cfg.backend ?? 'local');

  get<T>(key: string, defaultValue: T): T {
    return this.mode === 'blob'
      ? this.getFromBlob(key, defaultValue)
      : this.getFromKey(key, defaultValue);
  }

  set<T>(key: string, value: T): void {
    this.mode === 'blob' ? this.setInBlob(key, value) : this.setInKey(key, value);
  }

  remove(key: string): void {
    this.mode === 'blob' ? this.removeFromBlob(key) : this.storage.removeItem(this.mkKey(key));
  }

  clearNamespace(): void {
    if (this.mode === 'blob') {
      this.storage.removeItem(this.mkKey(this.cfg.blobKey ?? 'settings'));
      return;
    }
    const prefix = this.prefix();
    for (const k of this.storage.keys()) {
      if (k.startsWith(prefix)) this.storage.removeItem(k);
    }
  }

  signal<T>(key: string, defaultValue: T): WritableSignal<T> {
    const s = signal<T>(this.get<T>(key, defaultValue));
    effect(() => {
      const val = s();
      this.set(key, val);
    });
    return s;
  }

  private prefix(): string {
    return `${this.cfg.namespace}:`;
  }

  private mkKey(key: string): string {
    return `${this.prefix()}${key}`;
  }

  private getFromKey<T>(key: string, def: T): T {
    const raw = this.storage.getItem(this.mkKey(key));
    return raw == null ? def : safeParse<T>(raw, def);
  }

  private setInKey<T>(key: string, val: T): void {
    this.storage.setItem(this.mkKey(key), safeStringify(val));
  }

  private getFromBlob<T>(key: string, def: T): T {
    const blobKey = this.mkKey(this.cfg.blobKey ?? 'settings');
    const raw = this.storage.getItem(blobKey);
    const obj = raw ? safeParse<Record<string, unknown>>(raw, {}) : {};
    return (obj?.[key] as T) ?? def;
  }

  private setInBlob<T>(key: string, val: T): void {
    const blobKey = this.mkKey(this.cfg.blobKey ?? 'settings');
    const raw = this.storage.getItem(blobKey);
    const obj = raw ? safeParse<Record<string, unknown>>(raw, {}) : {};
    obj[key] = val;
    this.storage.setItem(blobKey, safeStringify(obj));
  }

  private removeFromBlob(key: string): void {
    const blobKey = this.mkKey(this.cfg.blobKey ?? 'settings');
    const raw = this.storage.getItem(blobKey);
    if (!raw) return;
    const obj = safeParse<Record<string, unknown>>(raw, {});
    delete obj[key];
    this.storage.setItem(blobKey, safeStringify(obj));
  }
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  keys(): string[];
}

function createStorage(kind: 'local' | 'session'): StorageLike {
  try {
    const s = kind === 'local' ? window.localStorage : window.sessionStorage;
    const testKey = '__test__';
    s.setItem(testKey, '1');
    s.removeItem(testKey);
    return {
      getItem: (k) => s.getItem(k),
      setItem: (k, v) => s.setItem(k, v),
      removeItem: (k) => s.removeItem(k),
      keys: () => Object.keys(s),
    };
  } catch {
    const map = new Map<string, string>();
    return {
      getItem: (k) => map.get(k) ?? null,
      setItem: (k, v) => map.set(k, v),
      removeItem: (k) => void map.delete(k),
      keys: () => Array.from(map.keys()),
    };
  }
}

function safeParse<T>(raw: string, fallback: T): T {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function safeStringify(val: unknown): string {
  try {
    return JSON.stringify(val);
  } catch {
    return 'null';
  }
}
